import { phaseWeekNumber, startOfCalendarWeek } from '~/calculations/calendar'
import { projectHasStarted } from '~/models/project'
import type { RulesOutcome } from '~/models/rulesOpinion'
import { isOpinion } from '~/models/rulesOpinion'
import type {
  PhaseSnapshot,
  WeeklyReviewDraftPatch,
  WeeklyReviewRecord,
} from '~/models/weeklyReview'
import { evaluateWeek } from '~/rules/engine'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import {
  createWeeklyReviewDraft,
  finalizeWeeklyReview,
  getDraftWeeklyReview,
  listWeeklyReviewsForWeek,
  updateWeeklyReviewDraft,
} from '~/storage/repositories/weeklyReviewRepository'
import type { CalendarDate } from '~/types/dates'
import type { WeeklyReviewId } from '~/types/ids'
import { err, ok, type Result } from '~/utils/result'
import { collectPlanSnapshot } from './planSnapshotService'
import { collectRulesInput } from './rulesService'
import { getEffectiveWeekStartDay } from './weekBoundaryService'
import { todayFor } from './weightService'

/**
 * The weekly review's impure boundary.
 *
 * The wizard talks only to this; everything that reads storage or the clock
 * happens here. The rules engine is still handed a plain snapshot, so it stays
 * deterministic and fixture-testable.
 */

/** The calendar week a date falls in, per the configured start day (D-034). */
export async function weekStartFor(
  db: SolaDatabase,
  now: Date,
): Promise<Result<CalendarDate, StorageError>> {
  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay
  return ok(startOfCalendarWeek(todayFor(now), weekStartDay.value))
}

/**
 * Open this week's review, resuming the draft if one exists.
 *
 * Resuming rather than starting fresh is the point of the whole checkpoint: a
 * reload, a backgrounded tab, or an iOS eviction must not cost the user the
 * answers they already gave.
 *
 * A **finished** week is not reopened. Finalizing is the moment a week stops
 * being editable (D-046), so a second visit to a completed week reports it as
 * complete rather than quietly starting a rival draft against the same dates.
 */
export type ReviewSession =
  | { readonly kind: 'draft'; readonly review: WeeklyReviewRecord }
  | { readonly kind: 'alreadyComplete'; readonly review: WeeklyReviewRecord }
  /**
   * The project has not begun (D-107 §4).
   *
   * A named state rather than an error: nothing went wrong, and the screen says
   * when the first review will be available.
   */
  | { readonly kind: 'projectNotStarted'; readonly startsOn: CalendarDate }

export async function startOrResumeWeeklyReview(
  db: SolaDatabase,
  now: Date,
): Promise<Result<ReviewSession, StorageError>> {
  /* Refused BEFORE `createWeeklyReviewDraft` below, which writes. Without this a
     user could open the review of a project that has not started, walk seven
     steps, and be told "Phase week number starts at 1." at finalize — with their
     answers saved and no way forward. That validator in `models/weeklyReview.ts`
     is the tripwire and stays exactly as it is; this is the fix. */
  const project = await getActiveProject(db)
  if (!project.ok) return project
  if (project.value !== undefined && !projectHasStarted(project.value, todayFor(now))) {
    return ok({ kind: 'projectNotStarted', startsOn: project.value.startDate })
  }

  const weekStart = await weekStartFor(db, now)
  if (!weekStart.ok) return weekStart

  const thisWeek = await listWeeklyReviewsForWeek(db, weekStart.value)
  if (!thisWeek.ok) return thisWeek

  const existingDraft = thisWeek.value.find((review) => review.status === 'draft')
  if (existingDraft !== undefined) return ok({ kind: 'draft', review: existingDraft })

  const finished = thisWeek.value.find((review) => review.status === 'complete')
  if (finished !== undefined) return ok({ kind: 'alreadyComplete', review: finished })

  const phase = await getActivePhase(db)
  if (!phase.ok) return phase

  const created = await createWeeklyReviewDraft(
    db,
    {
      weekStartDate: weekStart.value as string,
      ...(phase.value === undefined ? {} : { phaseId: phase.value.id }),
    },
    now,
  )
  if (!created.ok) return created
  return ok({ kind: 'draft', review: created.value })
}

/** Any draft in progress, whichever week it belongs to. */
export async function getResumableReview(
  db: SolaDatabase,
): Promise<Result<WeeklyReviewRecord | undefined, StorageError>> {
  return getDraftWeeklyReview(db)
}

/**
 * Persist one step's answers.
 *
 * Called on every step transition and by the registered draft saver, so the
 * worst a crash costs is the step currently on screen.
 */
export async function saveReviewStep(
  db: SolaDatabase,
  id: WeeklyReviewId,
  patch: WeeklyReviewDraftPatch,
  now: Date,
): Promise<Result<WeeklyReviewRecord, StorageError>> {
  return updateWeeklyReviewDraft(db, id, patch, now)
}

export type FinalizeReviewError =
  | StorageError
  /**
   * The engine produced no opinion, so there is nothing to attach to the week.
   *
   * Only reachable from restored or stale data: an unimplemented phase is not
   * selectable (D-043), and no active phase means setup is incomplete and the
   * wizard is unreachable. It is refused rather than finalized with an empty
   * opinion, because `PhaseRulesUnavailable` is explicitly not a recommendation
   * and must never be stored or exported as one.
   *
   * **What should happen to such a week is genuinely unspecified** — see L-014.
   * Refusing is the conservative reading and is reversible; storing a hollow
   * opinion would not be.
   */
  | { readonly kind: 'noOpinion'; readonly outcome: RulesOutcome; readonly message: string }

export interface FinalizedReview {
  readonly review: WeeklyReviewRecord
  readonly outcome: RulesOutcome
}

/**
 * Finish the week.
 *
 * Everything is computed before anything is written: the snapshot is read from
 * the live phase, the engine is run, and only then does the repository commit
 * the review, its `phaseSnapshot` and its opinion in one transaction. A failure
 * leaves the draft exactly as it was.
 */
export async function finalizeReview(
  db: SolaDatabase,
  id: WeeklyReviewId,
  now: Date,
): Promise<Result<FinalizedReview, FinalizeReviewError>> {
  const phase = await getActivePhase(db)
  if (!phase.ok) return err(phase.error)
  if (phase.value === undefined) {
    return err(
      storageError('invalidRecord', 'There is no active phase to judge this week against.'),
    )
  }

  const snapshot = await collectRulesInput(db, now)
  if (!snapshot.ok) return err(snapshot.error)

  const outcome = evaluateWeek(snapshot.value)
  if (!isOpinion(outcome)) {
    return err({
      kind: 'noOpinion',
      outcome,
      message:
        'Project Sola has no recommendation for this phase in this build, so this review cannot be finished yet. Your answers are saved.',
    })
  }

  const phaseSnapshot: PhaseSnapshot = {
    phaseType: phase.value.type,
    startDate: phase.value.startDate,
    plannedEndDate: phase.value.plannedEndDate,
    targetWeeklyChangeMinPercent: phase.value.targetWeeklyChangeMinPercent,
    targetWeeklyChangeMaxPercent: phase.value.targetWeeklyChangeMaxPercent,
    weekNumber: phaseWeekNumber(phase.value.startDate, snapshot.value.reference),
  }

  /* Frozen from the live plan here, and never rebuilt afterwards. A snapshot
     reconstructed later from the current plan would report what the user is
     doing now as though it were what they were asked to do then — the
     retroactive re-judgement D-046 forbids, in its newest costume. */
  const planSnapshot = await collectPlanSnapshot(db, now)
  if (!planSnapshot.ok) return err(planSnapshot.error)

  const written = await finalizeWeeklyReview(
    db,
    {
      reviewId: id,
      phaseSnapshot,
      ...(planSnapshot.value === undefined ? {} : { planSnapshot: planSnapshot.value }),
      opinion: outcome,
    },
    now,
  )
  if (!written.ok) return err(written.error)

  return ok({ review: written.value.review, outcome })
}
